// Currency formatting utilities

export const CURRENCIES = {
  SEK: { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', rate: 1 },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', rate: 0.087 },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', rate: 0.094 },
};

// Default/base currency for all prices (admin enters prices in SEK)
export const BASE_CURRENCY = 'SEK';

/**
 * Format price with currency symbol - always rounded to whole numbers
 * @param {number} price - The price amount in base currency (SEK)
 * @param {string} displayCurrency - Currency code to display in (EUR, USD, SEK)
 * @returns {string} Formatted price string
 */
export const formatPrice = (price, displayCurrency = 'SEK') => {
  const curr = CURRENCIES[displayCurrency] || CURRENCIES.SEK;
  const convertedPrice = Math.round(price * curr.rate);
  
  if (displayCurrency === 'SEK') {
    // Swedish Krona: symbol after amount
    return `${convertedPrice} ${curr.symbol}`;
  }
  
  // EUR and USD: symbol before amount
  return `${curr.symbol}${convertedPrice}`;
};

/**
 * Convert price from SEK to target currency
 * @param {number} priceInSek - Price in SEK
 * @param {string} targetCurrency - Target currency code
 * @returns {number} Converted price (rounded)
 */
export const convertPrice = (priceInSek, targetCurrency = 'SEK') => {
  const curr = CURRENCIES[targetCurrency] || CURRENCIES.SEK;
  return Math.round(priceInSek * curr.rate);
};

/**
 * Get currency symbol
 * @param {string} currency - Currency code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currency = 'EUR') => {
  return CURRENCIES[currency]?.symbol || '€';
};

/**
 * Parse size variant - handles both legacy (string) and new format (object)
 * @param {string|object} size - Size variant
 * @param {number} fallbackPrice - Fallback price for legacy format
 * @returns {object} Normalized size object {name, price}
 */
export const parseSizeVariant = (size, fallbackPrice = 0) => {
  if (typeof size === 'string') {
    // Legacy format: just a string name
    return {
      name: size,
      price: fallbackPrice,
    };
  }
  
  // New format: object with name, price
  return {
    name: size.name || 'Standard',
    price: size.price || fallbackPrice,
  };
};

/**
 * Get the lowest price from size variants for "From" display
 * @param {Array} sizes - Array of size variants
 * @param {number} basePrice - Base price fallback
 * @returns {number} Lowest price
 */
export const getLowestPrice = (sizes, basePrice) => {
  if (!sizes || sizes.length === 0) {
    return basePrice;
  }
  
  // Parse all sizes and find lowest price
  const parsed = sizes.map(s => parseSizeVariant(s, basePrice));
  const sorted = parsed.sort((a, b) => a.price - b.price);
  
  return sorted[0].price;
};

export default CURRENCIES;
